import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Save, Minus, Plus } from "lucide-react";
import { getBikes, getSelfGuidedRoutes, editBooking } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const F = ({ label, testid, ...p }) => (
  <label className="block"><span className="overline text-white/50">{label}</span><Input data-testid={testid} {...p} className="mt-1 h-10 bg-white/5 border-white/15 text-white" /></label>
);

const Qty = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <button data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(0, value - 1))} className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10"><Minus className="w-3.5 h-3.5" /></button>
    <span data-testid={`${testid}-qty`} className="w-6 text-center font-mono">{value}</span>
    <button data-testid={`${testid}-plus`} onClick={() => onChange(value + 1)} className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10"><Plus className="w-3.5 h-3.5" /></button>
  </div>
);

// Staff-only: change rider, time, bikes, tours or notes on a pending/confirmed booking
export const EditBookingDialog = ({ booking, onClose, onSaved }) => {
  const [bikes, setBikes] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getBikes().then(setBikes); getSelfGuidedRoutes().then(setRoutes); }, []);
  useEffect(() => {
    if (!booking) return setF(null);
    setF({
      customer_name: booking.customer_name, customer_email: booking.customer_email, customer_phone: booking.customer_phone || "",
      hire_date: booking.hire_date, start_time: booking.start_time || "", hours: booking.hours, notes: booking.notes || "",
      bikeQty: Object.fromEntries(booking.bikes.map((i) => [i.bike_id, i.qty])),
      tourQty: Object.fromEntries(booking.tours.map((t) => [t.route_id, t.qty])),
    });
  }, [booking]);

  const total = useMemo(() => {
    if (!f) return 0;
    const b = bikes.reduce((s, x) => s + (f.bikeQty[x.id] || 0) * x.price_per_hour * f.hours, 0);
    const t = routes.reduce((s, r) => { const n = f.tourQty[r.id] || 0; return s + (n > 0 ? (r.price || 0) + (n - 1) * 5 : 0); }, 0);
    return b + t;
  }, [f, bikes, routes]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await editBooking(booking.id, {
        customer_name: f.customer_name.trim(), customer_email: f.customer_email.trim(), customer_phone: f.customer_phone.trim(),
        hire_date: f.hire_date, start_time: f.start_time, hours: Number(f.hours), notes: f.notes,
        bikes: Object.entries(f.bikeQty).filter(([, q]) => q > 0).map(([bike_id, qty]) => ({ bike_id, qty })),
        tours: routes.filter((r) => (f.tourQty[r.id] || 0) > 0).map((r) => ({ route_id: r.id, qty: f.tourQty[r.id] })),
      });
      toast.success(`Saved ${r.ref} · new total $${r.total.toFixed(2)}${r.status === "confirmed" ? " · rider emailed" : ""}`);
      onSaved(r); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0A0A0A] text-[#F5F5F0] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="edit-booking-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl flex items-center gap-2"><Pencil className="w-5 h-5" /> Edit {booking?.ref}</DialogTitle></DialogHeader>
        {f && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Name" testid="edit-name" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} />
              <F label="Phone" testid="edit-phone" value={f.customer_phone} onChange={(e) => setF({ ...f, customer_phone: e.target.value })} />
              <F label="Email" testid="edit-email" type="email" value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })} />
              <div className="grid grid-cols-3 gap-2">
                <F label="Date" testid="edit-date" type="date" value={f.hire_date} onChange={(e) => setF({ ...f, hire_date: e.target.value })} />
                <F label="Start" testid="edit-time" type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} />
                <F label="Hours" testid="edit-hours" type="number" min={1} max={12} value={f.hours} onChange={(e) => setF({ ...f, hours: Math.min(12, Math.max(1, Number(e.target.value) || 1)) })} />
              </div>
            </div>
            <div>
              <p className="overline text-white/50 mb-2">Bikes</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {bikes.map((b) => (
                  <div key={b.id} className="flex items-center justify-between border border-white/10 rounded-lg px-3 py-2" data-testid={`edit-bike-${b.id}`}>
                    <div><p className="text-sm">{b.name}</p><p className="text-xs text-white/40 font-mono">${b.price_per_hour}/h</p></div>
                    <Qty value={f.bikeQty[b.id] || 0} onChange={(v) => setF({ ...f, bikeQty: { ...f.bikeQty, [b.id]: v } })} testid={`edit-bike-${b.id}`} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="overline text-white/50 mb-2">Self-guided tours</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {routes.map((r) => (
                  <div key={r.id} className="flex items-center justify-between border border-white/10 rounded-lg px-3 py-2" data-testid={`edit-tour-${r.id}`}>
                    <div><p className="text-sm">{r.title}</p><p className="text-xs text-white/40 font-mono">${r.price} Master Map · +$5 per extra rider</p></div>
                    <div className="text-right"><p className="text-[10px] text-white/40 uppercase tracking-wider">Riders with maps</p><Qty value={f.tourQty[r.id] || 0} onChange={(v) => setF({ ...f, tourQty: { ...f.tourQty, [r.id]: v } })} testid={`edit-tour-${r.id}`} /></div>
                  </div>
                ))}
              </div>
            </div>
            <label className="block"><span className="overline text-white/50">Notes</span><Textarea data-testid="edit-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="mt-1 bg-white/5 border-white/15 text-white" /></label>
            <div className="flex items-center justify-between gap-3 pt-1">
              <p className="font-mono text-sm text-white/60">New total <span className="text-white text-lg" data-testid="edit-total">${total.toFixed(2)}</span>{booking && Math.abs(total - booking.total) > 0.004 && <span className="text-white/40"> (was ${booking.total.toFixed(2)})</span>}</p>
              <Button data-testid="edit-save-btn" disabled={busy} onClick={save} className="rounded-full bg-[#F5F5F0] text-[#001A4D] hover:bg-white"><Save className="w-4 h-4 mr-1" /> Save changes</Button>
            </div>
            {booking?.status === "confirmed" && <p className="text-xs text-white/40">The rider will get a "Booking updated" email with the new details, codes and total to pay.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
